import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';

/** The refresh could not be attempted/completed because the issuer is unreachable. */
export class TokenRefreshUnavailableError extends Error {
  constructor(override readonly cause?: unknown) {
    super('Token refresh unavailable: issuer unreachable');
    this.name = 'TokenRefreshUnavailableError';
  }
}

function isTransientRefreshFailure(error: unknown): boolean {
  if (error instanceof TokenRefreshUnavailableError) {
    return true;
  }
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  const { status } = error;
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

/**
 * Recovers a Better Auth OIDC session after the browser throttles/suspends the
 * `angular-oauth2-oidc` `setupAutomaticSilentRefresh()` timer (backgrounded tab, OS
 * sleep). Entry points that call `ensureValidToken()`:
 *  - a `visibilitychange`/`focus` listener in `@et/auth`'s `oauth.initializer.ts`,
 *    proactively, as soon as the user returns to the app;
 *  - `@et/auth`'s `IdTokenBearerInterceptor`, reactively, on a 401 from a protected
 *    origin;
 *  - `JwtGuard`/`NativeJwtGuard`, before redirecting an expired-token navigation to
 *    `/auth/login`.
 *
 * All share the same in-flight promise so a focus event racing with an API call or a
 * navigation never triggers two concurrent `refreshToken()` calls.
 *
 * Lives in `@et/guards` (not `@et/auth`) so both guards and `@et/auth` can depend on
 * it without introducing a cycle — `@et/auth` already depends on `@et/guards`
 * transitively via `@et/api`.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshCoordinator {
  private readonly oauthService = inject(OAuthService, { optional: true });
  private readonly router = inject(Router);
  private refreshInFlight: Promise<boolean> | null = null;
  private discoveryLoader: (() => Promise<unknown>) | null = null;

  /**
   * Resolves `true` once the id_token is valid (immediately, or after a successful
   * refresh), `false` if no refresh could be performed or the refresh token itself
   * is no longer valid. Rejects with `TokenRefreshUnavailableError` when the issuer
   * could not be reached — the session is intact, so callers must NOT force re-login.
   */
  ensureValidToken(): Promise<boolean> {
    if (!this.oauthService) {
      return Promise.resolve(false);
    }
    if (this.oauthService.hasValidIdToken()) {
      return Promise.resolve(true);
    }
    /**
     * Nothing to renew with — the user never signed in, signed out, cleared
     * storage, or the refresh token aged out. `refreshToken()` does not check
     * this: it stringifies whatever `getRefreshToken()` returned into the form
     * body, so a missing token is POSTed as the literal `refresh_token=null`
     * and the issuer answers `401 invalid_grant`. Resolving `false` here reaches
     * the same outcome (callers route to login) without the doomed round trip.
     */
    if (!this.oauthService.getRefreshToken()) {
      return Promise.resolve(false);
    }
    if (!this.refreshInFlight) {
      const oauthService = this.oauthService;
      this.refreshInFlight = this.ensureDiscoveryLoaded(oauthService)
        // `refreshToken()` asserts `tokenEndpoint` *synchronously*, before it builds
        // its Promise. Invoking it inside `.then` turns that throw into a rejection.
        .then(() => oauthService.refreshToken())
        .then(() => {
          if (oauthService.hasValidIdToken()) {
            return true;
          }
          /**
           * Better Auth's legacy `oidcProvider` refresh_token grant rotates the
           * opaque access/refresh tokens but omits `id_token`. The SPA sends the
           * id_token as its API bearer, so a "successful" refresh leaves us with
           * an expired JWT and a brand-new refresh token — every subsequent 401 /
           * focus / navigation would refresh again forever. Clear local state so
           * callers force re-auth once instead of looping.
           */
          this.abandonUnusableSession(oauthService);
          return false;
        })
        .catch((error: unknown) => {
          if (isTransientRefreshFailure(error)) {
            // The issuer was unreachable/overloaded — that says nothing about the
            // refresh token. Keep the session so the next trigger can retry.
            throw new TokenRefreshUnavailableError(error);
          }
          this.abandonUnusableSession(oauthService);
          return false;
        })
        .finally(() => {
          this.refreshInFlight = null;
        });
    }
    return this.refreshInFlight;
  }

  /**
   * Registers how to (re)load the OIDC discovery document. `@et/auth` supplies it
   * because it owns the issuer URL; `@et/guards` cannot depend on `@et/auth`.
   */
  registerDiscoveryLoader(loader: () => Promise<unknown>): void {
    this.discoveryLoader = loader;
  }

  /**
   * If the boot-time discovery request failed (e.g. the Electron kiosk launched before
   * the network was up) `tokenEndpoint` stays empty for the life of the process, so
   * every later `refreshToken()` is dead on arrival. Retry the load on demand.
   */
  private async ensureDiscoveryLoaded(oauthService: OAuthService) {
    if (oauthService.tokenEndpoint || !this.discoveryLoader) {
      return;
    }
    try {
      await this.discoveryLoader();
    } catch (error) {
      throw new TokenRefreshUnavailableError(error);
    }
    if (!oauthService.tokenEndpoint) {
      throw new TokenRefreshUnavailableError(
        new Error('OIDC discovery document has no token endpoint'),
      );
    }
  }

  /**
   * The refresh token is dead (revoked/expired) — clear the local session without
   * hitting `end_session` (there is nothing valid left to end remotely) and send the
   * user back through login, preserving where they were.
   */
  forceReauth(returnUrl?: string): void {
    this.oauthService?.logOut(true);
    this.router.navigate(['/auth/login'], {
      queryParams: returnUrl ? { returnUrl } : {},
    });
  }

  /**
   * Stops automatic silent refresh and clears stored tokens so a half-refreshed
   * session (new refresh_token, still-expired id_token) cannot keep POSTing
   * `/oauth2/token`.
   */
  abandonUnusableSession(oauthService: OAuthService): void {
    oauthService.stopAutomaticRefresh();
    oauthService.logOut(true);
  }
}
